import { defineQuery, useQuery } from "@pinia/colada";
import { castArray, without } from "es-toolkit/compat";

const shopOptionsParsed = v.safeParse(shopOptionsSchema, shop_options);

type ShopParams = {
  page: number;
  per_page: number;
  sort_by: string;
  search?: string;
} & Record<string, string | string[] | number | number[]>;

type ShopChangedParam = {
  label: string;
  reset: () => void;
};

export const useShop = defineQuery(() => {
  if (!shopOptionsParsed.success) throw new Error("Les options de la boutique sont invalides");

  // Default filters, facet_by and query_by
  const { filters: defaultFilters, facet_by, query_by } = shopOptionsParsed.output;

  // Default and current (reactive) params
  const defaultParams = { page: 1, per_page: 9, sort_by: "timestamp:desc", search: "", ...Object.fromEntries(Object.entries(defaultFilters).map(([name, { default_value }]) => [name, default_value])) } as ShopParams;
  const { params, pause, resume, replaceState } = useSyncParams<ShopParams>(defaultParams);

  // Reset page to 1 when any other parameter changes
  watch(
    () => ({ ...params.value }),
    async (newParams, oldParams) => {
      if (newParams.page === oldParams.page) {
        pause();
        params.value.page = 1;
        await nextTick();
        resume();
        replaceState();
      }
    },
  );

  // Exposed params and their reset functions
  const changedParams = computed<ShopChangedParam[]>(() => {
    return Object.entries(params.value).reduce((result, [name, value]): ShopChangedParam[] => {
      if (
        ["page", "per_page", "sort_by"].includes(name)
        || JSON.stringify(value) === JSON.stringify(defaultParams[name])
      ) return result;
      const reset = () => params.value[name] = defaultParams[name];
      const values = castArray(value);

      // Handle search parameter
      if (name === "search" && typeof value === "string" && value.trim()) {
        return [...result, {
          label: `"${value.trim()}"`,
          reset,
        }];
      }

      switch (defaultFilters[name].component) {
        case "FilterCheckbox":
          if (defaultFilters[name].attrs.options.length === 0) {
            // Options defined by Typesense facet_counts
            return [
              ...result,
              ...(<string[]>values).map((label) => ({
                label,
                reset: () => params.value[name] = without(<string[]>params.value[name], label),
              })).filter(({ label }) => !(<string[]>defaultParams[name]).includes(label)),
            ];
          }
          else {
            // Options are boolean facets (i.e. stock > in_stock / pre_order, special > is_featured / on_sale)
            const options = defaultFilters[name].attrs.options;
            return [
              ...result,
              ...values.map((value) => ({
                label: options.find(({ value: v }) => v === value)?.label || "",
                reset: () => {
                  params.value[name] = without<string>(<string[]>castArray(params.value[name]), <string>value);
                },
              })),
            ];
          }
        case "FilterRange":
          return [...result, {
            label: `${values[0]} - ${values[1]} ${defaultFilters[name].attrs.suffix || ""}`.trim(),
            reset,
          }];
        case "FilterSlider":
          return [...result, {
            label: `${values[0]} ${defaultFilters[name].attrs.suffix || ""}`.trim(),
            reset,
          }];
        default:
          return result;
      }
    }, [] as ShopChangedParam[]);
  });

  // Typesense search parameters
  const search = computed(() => {
    const { page, per_page, sort_by, search: searchQuery, ...rest } = params.value;
    const filter_by = Object.entries(rest).reduce((result, [name, value]) => {
      if (!(name in defaultFilters) || !value) return result;
      const values = castArray(value);
      switch (defaultFilters[name].component) {
        case "FilterCheckbox":
          if (defaultFilters[name].attrs.options.length === 0) {
            // Options defined by Typesense facet_counts
            values.map((value) => result.push(`${name}:=\`${value}\``));
          }
          else {
            // Options are boolean facets (i.e. stock > in_stock / pre_order, special > is_featured / on_sale)
            values.map((value) => result.push(`${value}:=true`));
          }
          break;
        case "FilterRange":
          result.push(`${name}:[${values[0]}..${values[1]}]`);
          break;
        case "FilterSlider":
          if (values[0] === 0) break;
          result.push(`${name}_max:>=${values[0]}`);
          result.push(`${name}_min:<=${values[0]}`);
          break;
      }
      return result;
    }, [] as string[]);
    return {
      q: searchQuery && searchQuery.trim() ? searchQuery.trim() : "*",
      facet_by,
      query_by,
      page,
      per_page,
      sort_by,
      filter_by: filter_by.join(" && "),
    };
  });

  // Exposed filters from facets
  const filters = computed<ShopFilter[]>(() => {
    return Object.values(defaultFilters)
      .map((filter) => {
        switch (filter.component) {
          case "FilterCheckbox":
            if (filter.attrs.options.length === 0) {
              // Options defined by Typesense facet_counts
              const options = getFacetOptions(filter.name, shop.value.facets || [])
                .filter(({ value }) => !(<string[]>defaultFilters[filter.name].default_value).includes(value));
              return { ...filter, attrs: { options } };
            }
            else {
              // Options are boolean facets (i.e. stock > in_stock / pre_order, special > is_featured / on_sale)
              return { ...filter, attrs: { options: filter.attrs.options.map(({ value, label }) => ({ value, label, count: getFacetCount(value, "true", shop.value.facets || []) })) } };
            }
        }
        return filter;
      })
      .filter((filter) => filter.component !== "FilterCheckbox" || filter.attrs.options.length > 0);
  });

  // Typesense query
  const { data: shop, isLoading, status, error } = useQuery<Shop, Error, Shop>({
    key: () => ["shop", { ...search.value }],
    query: async () => {
      const response = await typesenseClient.collections<ProductDocument>("products").documents().search(search.value, {});
      return v.parse(shopSchema, {
        products: response.hits?.map(({ document }) => document) || [],
        products_total: response.found,
        facets: response.facet_counts || [],
      });
    },
    placeholderData: (previous) => previous || { products: [], products_total: 0, facets: [] },
  });

  return { params, changedParams, filters, shop, isLoading, status, error };
});

// Helper: Get facet options by field name (except option(s) in default_value)
function getFacetOptions(field_name: string, facets?: ShopFacet[]) {
  return facets?.find((facet) => facet.field_name === field_name)?.counts.map(({ value, count }) => ({ value, count })) || [];
}

// Helper: Get facet count by field name and value
function getFacetCount(field_name: string, value: string, facets?: ShopFacet[]) {
  return facets?.find((facet) => facet.field_name === field_name)?.counts.find(({ value: v }) => v === value)?.count || 0;
}
